import React, { useState, useRef } from 'react';
import { useModalDismiss } from '../../../hooks/useModalDismiss';
import axios from '../../../Api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faUpload, faSpinner, faCircleCheck, faTriangleExclamation, faFileExcel, faLocationDot } from '@fortawesome/free-solid-svg-icons';

const ERROR_GROUP_LABELS = {
  structure: 'Structure du fichier',
  columns: 'Colonnes',
  refs: 'Prestataires (Ref)',
  values: 'Valeurs / coordonnées',
  duplicates: 'Doublons',
  geocode: 'Géocodage',
  quota: 'Assignations (nombre d\'adresses)',
};

/**
 * CT upload of location addresses for a quota project. Upload -> server validates the
 * whole file (and geocodes rows missing coordinates) -> preview -> commit (upsert).
 * The base64 payload from the validated file is reused for commit (no stale re-read).
 */
const LocationImportModal = ({ quotaProjectId, onClose, onImportComplete }) => {
  // File-import form: only Escape and the explicit buttons dismiss it.
  const dismiss = useModalDismiss(onClose, { backdropClose: false });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [payload, setPayload] = useState(null); // { file_base64, filename } of the validated file
  const [committing, setCommitting] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const fileToBase64 = (f) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
    reader.onerror = () => reject(reader.error || new Error('read error'));
    reader.readAsDataURL(f);
  });

  const handleFileChange = (e) => {
    setFile(e.target.files?.[0] || null);
    setPreview(null);
    setPayload(null);
    setError('');
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setError('');
    setPreview(null);
    setPayload(null);

    let fileBase64;
    try {
      fileBase64 = await fileToBase64(file);
    } catch (readErr) {
      setError('Impossible de lire le fichier. Il a peut-être été modifié depuis sa sélection — veuillez le re-sélectionner.');
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      setUploading(false);
      return;
    }

    try {
      const body = { file_base64: fileBase64, filename: file.name };
      const response = await axios.post(`/quotas/projects/${quotaProjectId}/locations/preview`, body);
      setPreview(response.data);
      if (response.data.valid) setPayload(body);
    } catch (err) {
      if (err.response?.data?.errors) {
        const first = Object.values(err.response.data.errors)[0];
        setError(Array.isArray(first) ? first[0] : String(first));
      } else {
        setError(err.response?.data?.message || 'Erreur lors de l\'analyse du fichier.');
      }
    } finally {
      setUploading(false);
    }
  };

  const handleConfirm = async () => {
    if (!preview?.valid || !payload) return;
    setCommitting(true);
    setError('');
    try {
      const response = await axios.post(`/quotas/projects/${quotaProjectId}/locations/commit`, payload);
      if (onImportComplete) await onImportComplete(response.data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de l\'enregistrement des adresses.');
    } finally {
      setCommitting(false);
    }
  };

  const errorGroups = preview && preview.errors ? Object.entries(preview.errors) : [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" {...dismiss}>
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <FontAwesomeIcon icon={faLocationDot} className="text-red-600" />
            Importer les adresses (Excel)
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <FontAwesomeIcon icon={faTimes} className="text-xl" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          <p className="text-sm text-gray-600">
            Colonnes : <strong>Ref</strong>, puis pour une agence <strong>code_Agence, nom_agence</strong> ou sinon <strong>nom</strong>,
            avec <strong>Adresse</strong> et/ou <strong>Latitude/Longitude</strong>. Les coordonnées sont utilisées en priorité ;
            à défaut, l'adresse est géocodée (vérifiez le résultat sur la carte).
          </p>

          <div className="flex items-center gap-3">
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileChange}
              onClick={(e) => { e.target.value = null; }}
              className="block w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
            />
            <button
              onClick={handleUpload}
              disabled={!file || uploading}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {uploading ? (<><FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />Analyse...</>)
                : (<><FontAwesomeIcon icon={faUpload} className="mr-2" />Analyser</>)}
            </button>
          </div>

          {uploading && (
            <p className="text-xs text-gray-500">Analyse en cours (le géocodage des adresses sans coordonnées peut prendre un moment)…</p>
          )}

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
          )}

          {preview && !preview.valid && errorGroups.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-red-700 font-semibold text-sm">
                <FontAwesomeIcon icon={faTriangleExclamation} />
                Le fichier contient des erreurs — corrigez-les puis réimportez.
              </div>
              {errorGroups.map(([group, msgs]) => (
                <div key={group} className="border border-red-200 rounded-lg overflow-hidden">
                  <div className="px-3 py-1.5 bg-red-50 text-xs font-semibold text-red-800 uppercase tracking-wide">
                    {ERROR_GROUP_LABELS[group] || group} ({msgs.length})
                  </div>
                  <ul className="px-4 py-2 space-y-1 text-sm text-gray-700 list-disc list-inside">
                    {msgs.map((m, idx) => (<li key={idx}>{m}</li>))}
                  </ul>
                </div>
              ))}
            </div>
          )}

          {preview && preview.warnings && preview.warnings.length > 0 && (
            <div className="border border-amber-200 rounded-lg overflow-hidden">
              <div className="px-3 py-1.5 bg-amber-50 text-xs font-semibold text-amber-800 uppercase tracking-wide">
                Adresses géocodées ({preview.warnings.length}) — à vérifier
              </div>
              <ul className="px-4 py-2 space-y-1 text-sm text-gray-700 list-disc list-inside">
                {preview.warnings.map((m, idx) => (<li key={idx}>{m}</li>))}
              </ul>
            </div>
          )}

          {preview && preview.valid && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2 text-green-800 font-semibold text-sm mb-1">
                <FontAwesomeIcon icon={faCircleCheck} />
                Fichier valide
              </div>
              <p className="text-sm text-green-800">
                {preview.summary.locations} emplacement(s) pour {preview.summary.prestataires} prestataire(s)
                {preview.summary.geocoded > 0 ? `, dont ${preview.summary.geocoded} géocodé(s)` : ''} seront enregistrés / mis à jour.
              </p>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={committing}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            onClick={handleConfirm}
            disabled={!preview?.valid || committing || uploading}
            className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {committing ? 'Enregistrement...' : 'Confirmer l\'import'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LocationImportModal;
